import {
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { IBadge } from '../screens/Profile/screens/Leaderboard/types';
import { TM_SUPPORT_EMAIL } from '../shared/constants';
import { CustomRouter } from '../types/baseTypes';
import {
  HostMetadata,
  TitleConfig,
} from '../types/HostMetadata';
import { ROUTES } from '../types/routes';
import {
  IActiveUser,
  ICreatorCategory,
  PlanTierType,
} from '../types/userTypes';

interface ITagMeta {
  value: string;
  creatorId: string;
}

interface IAppSliceState {
  token: string | null;
  appReady: boolean;
  isLoggedIn: boolean;
  cookie: string | null;
  ip: string;
  categories: ICreatorCategory[];
  theme: 'light' | 'dark';
  userTheme: 'light' | 'dark' | 'system';
  hostMetadata: HostMetadata;
  hostDataFailedCount: number;
  userDetailsFailedCount: number;
  isTagMango: boolean;
  mangoTitle: string;
  mangoesTitle: string;
  shortUrl: string;
  deeplinkUrl: string;
  mDeeplinkUrl: string;
  deferredPrompt: any;
  isDRMUser: boolean;
  tags: Record<string, ITagMeta>;
  isGamificationEnabled?: boolean;
  isCommunityEnabled?: boolean;
  userTier: PlanTierType;
  isTier1: boolean;
  isTier2: boolean;
  isTier3: boolean;
  isTier4: boolean;
  customRouter: CustomRouter;
  titleConfig: TitleConfig;
  badges: IBadge[];
  badgesConfig: Partial<Record<string, IBadge>>;
  supportEmail?: string;
  activeUsers: IActiveUser[];
  courseTab?:string | null;
}

const defaultCustomRouter: CustomRouter = {
  homeRoutes: [
    {
      name: ROUTES.FEED,
      config: {
        title: 'Feed',
        path: '/activity',
        key: 'feed',
      },
    },
    {
      name: ROUTES.COURSES,
      config: {
        title: 'Courses',
        path: '/courses',
        key: 'courses',
      },
    },
    {
      name: ROUTES.VIDEO_CALL,
      config: {
        title: 'Video calls',
        path: '/video-call',
        key: 'video-call',
      },
    },
    {
      name: ROUTES.MESSAGES,
      config: {
        title: 'Messages',
        path: '/messages',
        key: 'messages',
      },
    },
  ],
  menuRoutes: [],
  initialRoutes: {
    default: {
      route: ROUTES.FEED,
      path: '/activity',
      isTMProject: true,
    },
  },
};

// Default title config to be used in the app
export const defaultTitleConfig: TitleConfig = {
  mango: 'mango',
  mangoes: 'mangoes',
  course: 'course',
  courses: 'courses',
  message: 'message',
  messages: 'messages',
  workshop: 'workshop',
  workshops: 'workshops',
};

const initialState: IAppSliceState = {
  token: null,
  appReady: false,
  isLoggedIn: false,
  cookie: null,
  ip: '',
  categories: [],
  theme: 'light',
  userTheme: 'light',
  hostMetadata: {
    __v: 0,
    _id: '',
    appName: 'TagMango',
    brandname: 'tagmango',
    createdAt: Date.now().toString(),
    creator: {
      _id: '',
      name: '',
      userSlug: '',
      userTier: 'tier1',
    },
    host: 'tagmango.com',
    logo: 'https://assets.website-files.com/6302e597fbb6ee54fbee3b8a/632aa73a05712d087d9bb4da_256x256.png',
    favicon:
      'https://assets.website-files.com/6302e597fbb6ee54fbee3b8a/632aa73a05712d087d9bb4da_256x256.png',
    offeringTitle: 'mango',
    offeringTitles: 'mangoes',
    updatedAt: Date.now().toString(),
    iosShareLink: '',
    androidShareLink: '',
  },
  hostDataFailedCount: 0,
  userDetailsFailedCount: 0,
  isTagMango: true,
  mangoTitle: 'mango',
  mangoesTitle: 'mangoes',
  shortUrl: 'https://tagmango.app/',
  deeplinkUrl: 'https://tagmango.com/',
  mDeeplinkUrl: 'https://m.tagmango.com/',
  deferredPrompt: null,
  isDRMUser: false,
  tags: {},
  badges: [],
  badgesConfig: {},
  isGamificationEnabled: false,
  userTier: 'tier1',
  isTier1: true,
  isTier2: false,
  isTier3: false,
  isTier4: false,
  customRouter: defaultCustomRouter,
  titleConfig: defaultTitleConfig,
  activeUsers: [],
  courseTab: null,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
    },
    setAppReady: (state, action: PayloadAction<boolean>) => {
      state.appReady = action.payload;
    },
    setLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setCookie: (state, action: PayloadAction<string | null>) => {
      state.cookie = action.payload;
    },
    setIp: (state, action: PayloadAction<string>) => {
      state.ip = action.payload;
    },
    setCategories: (state, action: PayloadAction<any[]>) => {
      Object.assign(state, { categories: action.payload });
    },
    setHostMetadata: (state, action: PayloadAction<Partial<HostMetadata>>) => {
      Object.assign(state.hostMetadata, {
        ...action.payload,
        offeringTitle: action.payload.offeringTitle || defaultTitleConfig.mango,
        offeringTitles:
          action.payload.offeringTitles || defaultTitleConfig.mangoes,
      });

      state.mangoTitle = (
        action.payload.offeringTitle || defaultTitleConfig.mango
      ).toLowerCase();
      state.mangoesTitle = (
        action.payload.offeringTitles || defaultTitleConfig.mangoes
      ).toLowerCase();

      state.isTagMango =
        state.hostMetadata.host.includes('tagmango.com') ||
        state.hostMetadata.host.includes('localhost');

      if (state.isTagMango) {
        state.supportEmail = TM_SUPPORT_EMAIL;
      }

      if (state.hostMetadata.supportAddress) {
        state.supportEmail = state.hostMetadata.supportAddress;
      } else {
        const supportType = state.hostMetadata?.supportWidget?.[0]?.type;
        const supportValue = state.hostMetadata?.supportWidget?.[0]?.value;

        if (supportType === 'email' && supportValue) {
          state.supportEmail = supportValue;
        } else {
          // clear support email if not added in the host by the creator
          state.supportEmail = undefined;
        }
      }

      if (
        action.payload.securityLevel &&
        typeof action.payload.securityLevel === 'number' &&
        action.payload.securityLevel > 1
      ) {
        state.isDRMUser = true;
      }
    },
    updateHostMetadata: (
      state,
      action: PayloadAction<Partial<HostMetadata>>,
    ) => {
      Object.assign(state.hostMetadata, action.payload);
    },
    setUserTier: (state, action: PayloadAction<PlanTierType>) => {
      state.userTier = action.payload;
      state.isTier1 = action.payload === 'tier1';
      state.isTier2 = action.payload === 'tier2';
      state.isTier3 = action.payload === 'tier3';
      state.isTier4 = action.payload === 'tier4';
    },
    setHostDataFailedCount: (state, action: PayloadAction<number>) => {
      state.hostDataFailedCount = action.payload;
    },
    setUserDetailsFailedCount: (state, action: PayloadAction<number>) => {
      state.userDetailsFailedCount = action.payload;
    },
    setIsGamificationEnabled: (state, action: PayloadAction<boolean>) => {
      state.isGamificationEnabled = action.payload;
    },
    setIsCommunityEnabled: (state, action: PayloadAction<boolean>) => {
      state.isCommunityEnabled = action.payload;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark' | undefined>) => {
      state.theme =
        action.payload || (state.theme === 'light' ? 'dark' : 'light');
    },
    setUserTheme: (
      state,
      action: PayloadAction<'light' | 'dark' | 'system'>,
    ) => {
      localStorage.setItem('theme', action.payload);
      state.userTheme = action.payload;
    },
    setNativeDomain: (state, action: PayloadAction<string>) => {
      state.shortUrl =
        action.payload.includes('tagmango.com') ||
        action.payload.includes('localhost')
          ? 'https://tagmango.app/'
          : `https://${action.payload}/l/`;
      state.deeplinkUrl = `https://${action.payload}/`;
      state.mDeeplinkUrl = `https://m.${action.payload}/`;
    },
    setDeferredPrompt: (state, action: PayloadAction<any>) => {
      state.deferredPrompt = action.payload;
    },
    setCustomRouter: (state, action: PayloadAction<Partial<CustomRouter>>) => {
      Object.assign(state.customRouter, action.payload);
    },
    setTags: (
      state,
      action: PayloadAction<
        { key: string; value: string; creatorId: string }[]
      >,
    ) => {
      const tags = action.payload.reduce(
        (acc, curr) => {
          acc[curr.key] = { value: curr.value, creatorId: curr.creatorId };
          return acc;
        },
        {} as Record<string, ITagMeta>,
      );
      state.tags = tags;
    },
    setTitleConfig: (state, action: PayloadAction<Partial<TitleConfig>>) => {
      Object.assign(state.titleConfig, action.payload);
    },
    setSupportEmail: (state, action: PayloadAction<string>) => {
      state.supportEmail = action.payload;
    },
    setBadges: (state, action: PayloadAction<IBadge[]>) => {
      const badges = [...action.payload];
      const badgesConfig = badges.reduce((acc, badge) => {
        Object.assign(acc, { [badge._id]: badge });

        return acc;
      }, {});

      state.badges = badges;
      state.badgesConfig = badgesConfig;
    },
    addNewActiveUser: (state, action: PayloadAction<IActiveUser>) => {
      const list = [...state.activeUsers, action.payload];
      const uniqueUsers = Array.from(new Set(list.map((user) => user._id))).map(
        (userId) => {
          return list.find((user) => user._id === userId);
        },
      );

      state.activeUsers = uniqueUsers.filter(
        (user) => user !== undefined,
      ) as IActiveUser[];
    },
    removeActiveUser: (state, action: PayloadAction<string>) => {
      state.activeUsers = state.activeUsers.filter(
        (user) => user._id !== action.payload,
      );
    },
    setCourseTab: (state, action: PayloadAction<string | null>) => {
      state.courseTab = action.payload;
    },
  },
});

export const {
  setToken,
  setAppReady,
  setLoggedIn,
  setCookie,
  setIp,
  setCategories,
  setTheme,
  setUserTheme,
  setHostMetadata,
  updateHostMetadata,
  setUserTier,
  setHostDataFailedCount,
  setUserDetailsFailedCount,
  setIsGamificationEnabled,
  setIsCommunityEnabled,
  setNativeDomain,
  setDeferredPrompt,
  setTags,
  setCustomRouter,
  setTitleConfig,
  setSupportEmail,
  setBadges,
  addNewActiveUser,
  removeActiveUser,
  setCourseTab
} = appSlice.actions;

export default appSlice.reducer;
